// src/pages/SuperAdmin/TenantsManager.jsx
import React, { useState } from 'react';
import { Row, Col, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Badge } from 'reactstrap';
import { post, put } from 'helpers/api_helper';
import cookieHelper from 'helpers/getCookieData';
import CountrySelector from 'components/Common/CountrySelector';
import CurrencySelector from 'components/Common/CurrencySelector';
import { formatMoney } from 'helpers/currencyHelper';

const TenantsManager = ({ tenants, plans, loading, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Add Tenant Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    clinic_name: '',
    doctor_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    country_code: 'IN',
    time_zone: 'Asia/Kolkata',
    currency_code: 'INR',
    zip_code: '',
    plan_id: '',
    billing_cycle: 'monthly',
    password: 'password123',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Manage Subscription Modal
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [subFormData, setSubFormData] = useState({
    plan_id: '',
    billing_cycle: 'monthly',
    duration_days: 30,
    notes: '',
  });

  // Manage Modules & Limits Modal
  const [isModulesModalOpen, setIsModulesModalOpen] = useState(false);
  const [modulesTenant, setModulesTenant] = useState(null);
  const [activeConfigTab, setActiveConfigTab] = useState('modules');
  const [selectedModules, setSelectedModules] = useState([]);
  const [customLimits, setCustomLimits] = useState({
    max_doctors: 5,
    max_patients: 5000,
    max_chairs: 5,
    max_employees: 10,
    max_clinics: 3,
    max_storage_mb: 5000,
  });
  const [savingModules, setSavingModules] = useState(false);

  const handleOpenModules = (tenant) => {
    setModulesTenant(tenant);
    setActiveConfigTab('modules');
    const currentFeatures = Array.isArray(tenant.effective_features) && tenant.effective_features.length > 0
      ? tenant.effective_features
      : Array.isArray(tenant.active_subscription?.plan?.features)
      ? tenant.active_subscription.plan.features
      : [
          "billing_accounts",
          "dental_chart",
          "prescriptions",
          "investigations_files",
          "clinical_notes",
          "multi_chair",
          "advanced_reports",
          "whatsapp_bot",
          "daily_briefing",
          "sms_reminders",
          "patient_portal",
          "kiosk"
        ];

    let parsedFeatures = currentFeatures;
    if (typeof currentFeatures === 'string') {
      try {
        parsedFeatures = JSON.parse(currentFeatures);
      } catch {
        parsedFeatures = [];
      }
    }
    setSelectedModules(Array.isArray(parsedFeatures) ? parsedFeatures : []);

    const existingLimits = tenant.effective_limits || {};
    const planLimits = tenant.active_subscription?.plan || {};
    setCustomLimits({
      max_doctors: existingLimits.max_doctors ?? planLimits.max_doctors ?? 5,
      max_patients: existingLimits.max_patients ?? planLimits.max_patients ?? 5000,
      max_chairs: existingLimits.max_chairs ?? planLimits.max_chairs ?? 5,
      max_employees: existingLimits.max_employees ?? 10,
      max_clinics: existingLimits.max_clinics ?? 3,
      max_storage_mb: existingLimits.max_storage_mb ?? planLimits.max_storage_mb ?? 5000,
    });

    setIsModulesModalOpen(true);
  };

  const handleToggleModule = (key) => {
    setSelectedModules(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSaveModules = async (e) => {
    e.preventDefault();
    if (!modulesTenant) return;
    setSavingModules(true);
    try {
      await put(`/superadmin/tenants/${modulesTenant.id}/modules`, {
        enabled_modules: selectedModules,
        custom_limits: customLimits,
      });
      setIsModulesModalOpen(false);
      setModulesTenant(null);
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Failed to update tenant modules and limits');
    } finally {
      setSavingModules(false);
    }
  };

  const filteredTenants = tenants.filter((t) => {
    const matchSearch =
      (t.clinic_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.client?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.client?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.city || '').toLowerCase().includes(searchTerm.toLowerCase());

    const isSubActive = t.active_subscription?.status === 'active';
    const isSubTrial = t.active_subscription?.status === 'trial';
    const isSubExpired = t.active_subscription?.status === 'expired';

    if (statusFilter === 'active') return matchSearch && isSubActive && Number(t.status) === 0;
    if (statusFilter === 'trial') return matchSearch && isSubTrial && Number(t.status) === 0;
    if (statusFilter === 'expired') return matchSearch && isSubExpired && Number(t.status) === 0;
    if (statusFilter === 'suspended') return matchSearch && Number(t.status) === 1;

    return matchSearch;
  });

  // Handle Toggle Clinic Active / Suspended Status
  const handleToggleStatus = async (clinicId, currentStatus) => {
    const isCurrentlySuspended = Number(currentStatus) === 1;
    const confirmMsg = isCurrentlySuspended
      ? 'Are you sure you want to activate this clinic?'
      : 'Are you sure you want to suspend this clinic? All logins and clinical activities will be blocked.';
    if (!window.confirm(confirmMsg)) return;

    try {
      const newStatus = isCurrentlySuspended ? 0 : 1;
      await put(`/superadmin/tenants/${clinicId}/status`, { status: newStatus });
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Error updating status');
    }
  };

  // Handle Impersonate Tenant
  const handleImpersonate = async (clinicId) => {
    if (!window.confirm('Are you sure you want to log in as this clinic administrator?')) return;
    try {
      const res = await post(`/superadmin/tenants/${clinicId}/impersonate`, {});
      const resData = res?.body || res?.data || res;
      if (resData?.token && resData?.clinic) {
        const { token, clinic, user } = resData;
        // Set clinic cookie and redirect to main dashboard
        cookieHelper.setCookie('_c', btoa(JSON.stringify(clinic)));
        localStorage.setItem('authUser', JSON.stringify({ token, user }));
        window.location.href = '/dashboard';
      }
    } catch (err) {
      alert(err?.message || 'Failed to impersonate clinic');
    }
  };

  // Handle Create New Tenant
  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');
    try {
      await post('/superadmin/tenants', formData);
      setIsAddModalOpen(false);
      setFormData({
        clinic_name: '',
        doctor_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        plan_id: '',
        billing_cycle: 'monthly',
        password: 'password123',
      });
      onRefresh();
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Assign Subscription
  const handleAssignSub = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setSubmitting(true);
    try {
      await post('/superadmin/subscriptions/assign', {
        clinic_id: selectedTenant.id,
        ...subFormData,
      });
      setIsSubModalOpen(false);
      setSelectedTenant(null);
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Error assigning subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Search & Actions Bar */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div className="d-flex gap-2 flex-grow-1 max-w-lg">
          <div className="position-relative flex-grow-1">
            <Input
              type="text"
              placeholder="Search by clinic name, doctor, email, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-4 font-size-13 shadow-none border-light-subtle"
            />
            <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-2.5 text-muted" />
          </div>

          <Input
            type="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-auto font-size-13 shadow-none border-light-subtle"
          >
            <option value="all">All Status</option>
            <option value="active">Active Paid</option>
            <option value="trial">Free Trial</option>
            <option value="expired">Expired</option>
            <option value="suspended">Suspended</option>
          </Input>
        </div>

        <div className="d-flex gap-2">
          <Button color="light" onClick={onRefresh} className="btn-sm d-flex align-items-center gap-1 shadow-none">
            <i className="bx bx-refresh font-size-16" /> Refresh
          </Button>
          <Button color="warning" onClick={() => setIsAddModalOpen(true)} className="btn-sm d-flex align-items-center gap-1 text-dark font-weight-semibold shadow-xs">
            <i className="bx bx-plus font-size-16" /> Register New Tenant
          </Button>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="card shadow-2xs border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0 table-hover">
              <thead className="table-light font-size-12 text-uppercase text-muted">
                <tr>
                  <th>Clinic & Location</th>
                  <th>Primary Doctor / Owner</th>
                  <th>Resource Usage</th>
                  <th>Subscription Tier</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      {loading ? 'Loading tenants...' : 'No clinics match the search filters.'}
                    </td>
                  </tr>
                ) : (
                  filteredTenants.map((tenant) => {
                    const sub = tenant.active_subscription;
                    const isSuspended = Number(tenant.status) === 1;
                    const planName = sub?.plan?.name || (sub?.status === 'trial' ? 'Free Trial' : 'No Active Plan');
                    const isTrial = sub?.status === 'trial';
                    const isActive = sub?.status === 'active';

                    return (
                      <tr key={tenant.id} className={isSuspended ? 'opacity-75 bg-light' : ''}>
                        <td>
                          <div className="font-weight-semibold text-dark font-size-13">{tenant.clinic_name}</div>
                          <div className="text-muted font-size-11">
                            <i className="bx bx-map pin me-1" />
                            {tenant.city || 'City not set'}{tenant.state ? `, ${tenant.state}` : ''}
                          </div>
                        </td>

                        <td>
                          <div className="font-weight-medium font-size-12 text-dark">{tenant.client?.name || 'Administrator'}</div>
                          <div className="text-muted font-size-11">{tenant.email || tenant.client?.email || '-'}</div>
                          <div className="text-muted font-size-11">{tenant.phone || tenant.client?.mobile || '-'}</div>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-1.5 font-size-11 flex-wrap">
                            <Badge color="light" className="text-dark border font-size-10">
                              <i className="bx bx-user-pin text-primary me-1" />
                              {tenant.usage?.doctors || 0}/{tenant.effective_limits?.max_doctors === -1 ? '∞' : (tenant.effective_limits?.max_doctors ?? '5')} Docs
                            </Badge>
                            <Badge color="light" className="text-dark border font-size-10">
                              <i className="bx bx-chair text-warning me-1" />
                              {tenant.usage?.chairs || 0}/{tenant.effective_limits?.max_chairs === -1 ? '∞' : (tenant.effective_limits?.max_chairs ?? '5')} Chairs
                            </Badge>
                            <Badge color="light" className="text-dark border font-size-10">
                              <i className="bx bx-group text-success me-1" />
                              {tenant.usage?.patients || 0}/{tenant.effective_limits?.max_patients === -1 ? '∞' : (tenant.effective_limits?.max_patients ?? '5k')} Pts
                            </Badge>
                          </div>
                        </td>

                        <td>
                          <div className="font-weight-semibold font-size-12">{planName}</div>
                          <div className="font-size-11 text-muted">
                            {sub?.expiry_date ? `Renews: ${new Date(sub.expiry_date).toLocaleDateString()}` : 'Lifetime / No Expiry'}
                          </div>
                        </td>

                        <td>
                          {isSuspended ? (
                            <span className="status-badge suspended">
                              <i className="bx bx-block" /> Suspended
                            </span>
                          ) : (
                            <span className={`status-badge ${isActive ? 'active' : isTrial ? 'trial' : 'expired'}`}>
                              <i className={`bx ${isActive ? 'bx-check-circle' : isTrial ? 'bx-time-five' : 'bx-error-circle'}`} />
                              {isActive ? 'Active Paid' : isTrial ? 'Free Trial' : 'Expired'}
                            </span>
                          )}
                        </td>

                        <td className="text-end">
                          <div className="d-inline-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-dark"
                              title={isSuspended ? 'Clinic is suspended' : 'Log In As Clinic Administrator'}
                              disabled={isSuspended}
                              onClick={() => handleImpersonate(tenant.id)}
                            >
                              <i className="bx bx-log-in-circle me-1" /> Login As
                            </button>

                            <button
                              type="button"
                              className="btn btn-xs btn-outline-warning text-dark font-weight-semibold"
                              title="Configure Active Modules & Resource Limits for this Tenant"
                              onClick={() => handleOpenModules(tenant)}
                            >
                              <i className="bx bx-slider-alt me-1" /> Config & Limits
                            </button>

                            <button
                              type="button"
                              className="btn btn-xs btn-outline-primary"
                              title="Manage Subscription"
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setSubFormData({
                                  plan_id: tenant.active_subscription?.plan_id || plans[0]?.id || '',
                                  billing_cycle: tenant.active_subscription?.billing_cycle || 'monthly',
                                  duration_days: 30,
                                  notes: '',
                                });
                                setIsSubModalOpen(true);
                              }}
                            >
                              <i className="bx bx-credit-card me-1" /> Plan
                            </button>

                            <button
                              type="button"
                              className={`btn btn-xs ${isSuspended ? 'btn-outline-success' : 'btn-outline-danger'}`}
                              title={isSuspended ? 'Activate Clinic' : 'Suspend Clinic'}
                              onClick={() => handleToggleStatus(tenant.id, tenant.status)}
                            >
                              <i className={`bx ${isSuspended ? 'bx-check' : 'bx-block'}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* Add New Clinic Tenant Modal */}
      <Modal isOpen={isAddModalOpen} toggle={() => setIsAddModalOpen(!isAddModalOpen)} size="lg" centered>
        <ModalHeader toggle={() => setIsAddModalOpen(!isAddModalOpen)}>
          <i className="bx bx-buildings text-warning me-2" /> Register New Dental Clinic Tenant
        </ModalHeader>
        <Form onSubmit={handleCreateTenant}>
          <ModalBody>
            {errorMessage && <div className="alert alert-danger py-2">{errorMessage}</div>}

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Clinic Name *</Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. Apex Dental Hospital"
                    value={formData.clinic_name}
                    onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Practitioner / Doctor Name *</Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. Dr. Jane Smith"
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Email Address *</Label>
                  <Input
                    type="email"
                    required
                    placeholder="e.g. doctor@clinic.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Mobile / Phone *</Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. 9876543210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Country *</Label>
                  <CountrySelector
                    value={formData.country_code}
                    onCountrySelect={(defaults) => {
                      setFormData({
                        ...formData,
                        country: defaults.name,
                        country_code: defaults.code,
                        time_zone: defaults.default_timezone,
                        currency_code: defaults.default_currency,
                      });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Timezone (Suggested) *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.time_zone}
                    onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Operating Currency *</Label>
                  <CurrencySelector
                    value={formData.currency_code}
                    onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">City</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Mumbai"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">State</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Maharashtra"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Initial Subscription Tier</Label>
                  <Input
                    type="select"
                    value={formData.plan_id}
                    onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                  >
                    <option value="">Default (Free Trial - 14 Days)</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (₹{p.price_monthly}/mo)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Temporary Owner Password</Label>
                  <Input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Register Tenant'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Tenant Module & Resource Limits Configurator Modal */}
      <Modal isOpen={isModulesModalOpen} toggle={() => setIsModulesModalOpen(!isModulesModalOpen)} size="lg" centered>
        <ModalHeader toggle={() => setIsModulesModalOpen(!isModulesModalOpen)}>
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-slider-alt text-warning font-size-22" />
            <div>
              <div className="font-size-15 font-weight-bold text-dark">Tenant Configuration & Resource Limits</div>
              <div className="font-size-11 text-muted font-weight-normal">
                Clinic: <strong className="text-dark">{modulesTenant?.clinic_name}</strong> &bull; Plan: <span className="badge bg-soft-primary text-primary">{modulesTenant?.active_subscription?.plan?.name || 'Growth Plan'}</span>
              </div>
            </div>
          </div>
        </ModalHeader>
        <Form onSubmit={handleSaveModules}>
          <ModalBody className="p-3">
            {/* Modal Tabs Navigation & Subscription Tier Mapping Bar */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 border-bottom pb-2">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className={`btn btn-sm ${activeConfigTab === 'modules' ? 'btn-warning text-dark font-weight-bold shadow-xs' : 'btn-light text-muted'}`}
                  onClick={() => setActiveConfigTab('modules')}
                >
                  <i className="bx bx-grid-alt me-1" />
                  Active Modules & Features ({selectedModules.length}/12)
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${activeConfigTab === 'limits' ? 'btn-warning text-dark font-weight-bold shadow-xs' : 'btn-light text-muted'}`}
                  onClick={() => setActiveConfigTab('limits')}
                >
                  <i className="bx bx-tachometer me-1" />
                  Capacity Quotas & Limits
                </button>
              </div>

              {/* Map from Subscription Tier Presets */}
              <div className="d-flex align-items-center gap-1.5 font-size-11">
                <span className="text-muted font-weight-semibold">
                  <i className="bx bx-sync text-primary me-1" />
                  Map from Tier:
                </span>
                {plans.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="btn btn-xs btn-soft-secondary text-dark"
                    title={`Apply ${p.name} modules and resource limits`}
                    onClick={() => {
                      let tierFeatures = [];
                      try {
                        tierFeatures = typeof p.features === 'string' ? JSON.parse(p.features) : (p.features || []);
                      } catch {
                        tierFeatures = [];
                      }
                      setSelectedModules(Array.isArray(tierFeatures) ? tierFeatures : []);
                      setCustomLimits({
                        max_doctors: p.max_doctors ?? 5,
                        max_patients: p.max_patients ?? 5000,
                        max_chairs: p.max_chairs ?? 5,
                        max_employees: 10,
                        max_clinics: 3,
                        max_storage_mb: p.max_storage_mb ?? 5000,
                      });
                    }}
                  >
                    {p.name.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>

            {/* TAB 1: MODULES & FEATURES */}
            {activeConfigTab === 'modules' && (
              <>
                <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2.5 rounded">
                  <div className="font-size-12 text-muted">
                    <i className="bx bx-info-circle text-primary me-1" />
                    Modules disabled here will automatically hide all related navigation menus, fields, tabs, and reports for this clinic.
                  </div>
                  <div className="d-flex gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-dark"
                      onClick={() => {
                        const allKeys = [
                          "billing_accounts",
                          "dental_chart",
                          "prescriptions",
                          "investigations_files",
                          "clinical_notes",
                          "multi_chair",
                          "advanced_reports",
                          "whatsapp_bot",
                          "daily_briefing",
                          "sms_reminders",
                          "patient_portal",
                          "kiosk"
                        ];
                        setSelectedModules(allKeys);
                      }}
                    >
                      Enable All
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-secondary"
                      onClick={() => setSelectedModules([])}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <Row className="g-2.5">
                  {[
                    {
                      key: "billing_accounts",
                      label: "Billing, Invoicing, Payments & Ledger",
                      category: "Finance",
                      desc: "Invoices, payments, receipts, balance tracking, and ledger",
                      icon: "bx bx-credit-card",
                      badgeColor: "success"
                    },
                    {
                      key: "dental_chart",
                      label: "Interactive Dental & Periodontal Chart",
                      category: "Clinical",
                      desc: "Visual tooth charts, surfaces, and perio examinations",
                      icon: "bx bx-face",
                      badgeColor: "primary"
                    },
                    {
                      key: "prescriptions",
                      label: "Digital Prescriptions & Rx Generation",
                      category: "Clinical",
                      desc: "Medicine database, dosages, and printed digital Rx",
                      icon: "bx bx-file",
                      badgeColor: "primary"
                    },
                    {
                      key: "investigations_files",
                      label: "Lab Investigations & File Uploads",
                      category: "Clinical",
                      desc: "X-rays, lab orders, radiology reports, and attachments",
                      icon: "bx bx-folder",
                      badgeColor: "primary"
                    },
                    {
                      key: "clinical_notes",
                      label: "Clinical Notes & Medical History",
                      category: "Clinical",
                      desc: "Doctor progress notes, systemic history, and allergies",
                      icon: "bx bx-notepad",
                      badgeColor: "primary"
                    },
                    {
                      key: "multi_chair",
                      label: "Multi-Chair Scheduling & OPD",
                      category: "Operations",
                      desc: "Chair switching, timeline view, and room management",
                      icon: "bx bx-table",
                      badgeColor: "warning"
                    },
                    {
                      key: "advanced_reports",
                      label: "Analytics & Advanced Reports",
                      category: "Intelligence",
                      desc: "Performance metrics, collection charts, and doctor analytics",
                      icon: "bx bx-line-chart",
                      badgeColor: "info"
                    },
                    {
                      key: "whatsapp_bot",
                      label: "WhatsApp Bot & Automations",
                      category: "Communication",
                      desc: "Instant appointment confirmations and reminder messages",
                      icon: "bx bxl-whatsapp",
                      badgeColor: "success"
                    },
                    {
                      key: "daily_briefing",
                      label: "Daily Briefing & Live Digest",
                      category: "Communication",
                      desc: "Automated morning schedule and evening summary reports",
                      icon: "bx bx-bell",
                      badgeColor: "secondary"
                    },
                    {
                      key: "sms_reminders",
                      label: "SMS Notifications & Reminders",
                      category: "Communication",
                      desc: "Direct SMS alerts to patients for upcoming appointments",
                      icon: "bx bx-message-rounded-dots",
                      badgeColor: "secondary"
                    },
                    {
                      key: "patient_portal",
                      label: "Patient Self-Service Web Portal",
                      category: "Patient Experience",
                      desc: "Patient portal for appointment history, bills, and Rx access",
                      icon: "bx bx-user-circle",
                      badgeColor: "info"
                    },
                    {
                      key: "kiosk",
                      label: "Self Check-in Clinic Kiosk",
                      category: "Patient Experience",
                      desc: "Reception tablet kiosk mode for touchless patient check-in",
                      icon: "bx bx-devices",
                      badgeColor: "info"
                    }
                  ].map(m => {
                    const isEnabled = selectedModules.includes(m.key);
                    return (
                      <Col md={6} key={m.key}>
                        <div
                          className={`p-2.5 rounded border transition-all cursor-pointer d-flex align-items-start gap-2.5 ${
                            isEnabled ? 'bg-white border-warning shadow-2xs' : 'bg-light bg-opacity-50 opacity-75'
                          }`}
                          onClick={() => handleToggleModule(m.key)}
                          style={{ minHeight: '80px' }}
                        >
                          <div className="form-check form-switch pt-1">
                            <input
                              type="checkbox"
                              className="form-check-input cursor-pointer"
                              checked={isEnabled}
                              onChange={() => handleToggleModule(m.key)}
                              id={`mod-${m.key}`}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <label htmlFor={`mod-${m.key}`} className="font-size-12 font-weight-bold text-dark m-0 cursor-pointer">
                                <i className={`${m.icon} text-warning me-1`} />
                                {m.label}
                              </label>
                              <span className={`badge bg-soft-${m.badgeColor} text-${m.badgeColor} font-size-10`}>
                                {m.category}
                              </span>
                            </div>
                            <div className="font-size-11 text-muted">
                              {m.desc}
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}

            {/* TAB 2: CAPACITY QUOTAS & RESOURCE LIMITS */}
            {activeConfigTab === 'limits' && (
              <>
                <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2.5 rounded">
                  <div className="font-size-12 text-muted">
                    <i className="bx bx-shield-quarter text-warning me-1" />
                    Configure strict capacity caps per tenant. Set value to <strong>-1</strong> for Unlimited capacity.
                  </div>
                  <div className="d-flex gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-primary"
                      onClick={() => setCustomLimits({
                        max_doctors: 2,
                        max_patients: 1000,
                        max_chairs: 2,
                        max_employees: 4,
                        max_clinics: 1,
                        max_storage_mb: 2000,
                      })}
                    >
                      Starter Limits
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-warning text-dark font-weight-semibold"
                      onClick={() => setCustomLimits({
                        max_doctors: 8,
                        max_patients: 5000,
                        max_chairs: 6,
                        max_employees: 15,
                        max_clinics: 3,
                        max_storage_mb: 10000,
                      })}
                    >
                      Growth Limits
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-success"
                      onClick={() => setCustomLimits({
                        max_doctors: -1,
                        max_patients: -1,
                        max_chairs: -1,
                        max_employees: -1,
                        max_clinics: -1,
                        max_storage_mb: -1,
                      })}
                    >
                      Unlimited (-1)
                    </button>
                  </div>
                </div>

                <Row className="g-3">
                  {/* Max Doctors Limit */}
                  <Col md={6}>
                    <div className="p-3 border rounded bg-white h-100 shadow-2xs">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-size-13 font-weight-bold text-dark m-0 d-flex align-items-center gap-1.5">
                          <i className="bx bx-user-pin text-primary font-size-16" />
                          Max Doctors Limit
                        </label>
                        <span className="badge bg-soft-info text-info font-size-10">
                          Active: {modulesTenant?.usage?.doctors || 0} docs
                        </span>
                      </div>
                      <Input
                        type="number"
                        className="form-control font-weight-semibold"
                        value={customLimits.max_doctors}
                        onChange={(e) => setCustomLimits({ ...customLimits, max_doctors: Number(e.target.value) })}
                        placeholder="e.g. 5 (-1 for Unlimited)"
                      />
                      <small className="text-muted font-size-11 d-block mt-1">
                        Maximum number of dental surgeon & practitioner profiles.
                      </small>
                    </div>
                  </Col>

                  {/* Max Patients Limit */}
                  <Col md={6}>
                    <div className="p-3 border rounded bg-white h-100 shadow-2xs">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-size-13 font-weight-bold text-dark m-0 d-flex align-items-center gap-1.5">
                          <i className="bx bx-group text-success font-size-16" />
                          Max Patients Limit
                        </label>
                        <span className="badge bg-soft-success text-success font-size-10">
                          Active: {modulesTenant?.usage?.patients || 0} pts
                        </span>
                      </div>
                      <Input
                        type="number"
                        className="form-control font-weight-semibold"
                        value={customLimits.max_patients}
                        onChange={(e) => setCustomLimits({ ...customLimits, max_patients: Number(e.target.value) })}
                        placeholder="e.g. 5000 (-1 for Unlimited)"
                      />
                      <small className="text-muted font-size-11 d-block mt-1">
                        Total active registered EMR patient records in clinic database.
                      </small>
                    </div>
                  </Col>

                  {/* Max Chairs / Operatories Limit */}
                  <Col md={6}>
                    <div className="p-3 border rounded bg-white h-100 shadow-2xs">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-size-13 font-weight-bold text-dark m-0 d-flex align-items-center gap-1.5">
                          <i className="bx bx-chair text-warning font-size-16" />
                          Max Operatory Chairs Limit
                        </label>
                        <span className="badge bg-soft-warning text-dark font-size-10">
                          Active: {modulesTenant?.usage?.chairs || 0} chairs
                        </span>
                      </div>
                      <Input
                        type="number"
                        className="form-control font-weight-semibold"
                        value={customLimits.max_chairs}
                        onChange={(e) => setCustomLimits({ ...customLimits, max_chairs: Number(e.target.value) })}
                        placeholder="e.g. 4 (-1 for Unlimited)"
                      />
                      <small className="text-muted font-size-11 d-block mt-1">
                        Dental operatory chairs & treatment rooms in operatory grid.
                      </small>
                    </div>
                  </Col>

                  {/* Max Employees / Staff Limit */}
                  <Col md={6}>
                    <div className="p-3 border rounded bg-white h-100 shadow-2xs">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-size-13 font-weight-bold text-dark m-0 d-flex align-items-center gap-1.5">
                          <i className="bx bx-id-card text-purple font-size-16" />
                          Max Employees & Staff Limit
                        </label>
                        <span className="badge bg-soft-secondary text-secondary font-size-10">
                          Active: {modulesTenant?.usage?.employees || 0} staff
                        </span>
                      </div>
                      <Input
                        type="number"
                        className="form-control font-weight-semibold"
                        value={customLimits.max_employees}
                        onChange={(e) => setCustomLimits({ ...customLimits, max_employees: Number(e.target.value) })}
                        placeholder="e.g. 10 (-1 for Unlimited)"
                      />
                      <small className="text-muted font-size-11 d-block mt-1">
                        Receptionists, dental assistants, billing cashiers, and nursing staff.
                      </small>
                    </div>
                  </Col>

                  {/* Max Clinics / Branches Limit */}
                  <Col md={6}>
                    <div className="p-3 border rounded bg-white h-100 shadow-2xs">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-size-13 font-weight-bold text-dark m-0 d-flex align-items-center gap-1.5">
                          <i className="bx bx-buildings text-danger font-size-16" />
                          Max Clinics / Branches Limit
                        </label>
                        <span className="badge bg-soft-danger text-danger font-size-10">
                          Active: {modulesTenant?.usage?.clinics || 1} branch
                        </span>
                      </div>
                      <Input
                        type="number"
                        className="form-control font-weight-semibold"
                        value={customLimits.max_clinics}
                        onChange={(e) => setCustomLimits({ ...customLimits, max_clinics: Number(e.target.value) })}
                        placeholder="e.g. 3 (-1 for Unlimited)"
                      />
                      <small className="text-muted font-size-11 d-block mt-1">
                        Multi-location dental branches allowed under this tenant account.
                      </small>
                    </div>
                  </Col>

                  {/* Max Cloud Storage (MB) */}
                  <Col md={6}>
                    <div className="p-3 border rounded bg-white h-100 shadow-2xs">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-size-13 font-weight-bold text-dark m-0 d-flex align-items-center gap-1.5">
                          <i className="bx bx-cloud text-info font-size-16" />
                          Cloud Storage Quota (MB)
                        </label>
                        <span className="badge bg-soft-info text-info font-size-10">
                          {customLimits.max_storage_mb === -1 ? 'Unlimited' : `${customLimits.max_storage_mb || 5000} MB`}
                        </span>
                      </div>
                      <Input
                        type="number"
                        className="form-control font-weight-semibold"
                        value={customLimits.max_storage_mb}
                        onChange={(e) => setCustomLimits({ ...customLimits, max_storage_mb: Number(e.target.value) })}
                        placeholder="e.g. 5000 MB (-1 for Unlimited)"
                      />
                      <small className="text-muted font-size-11 d-block mt-1">
                        Storage allocated for patient X-rays, CBCT scans, and clinical documents.
                      </small>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIsModulesModalOpen(false)}>
              Cancel
            </Button>
            <Button color="warning" className="text-dark font-weight-bold px-4" type="submit" disabled={savingModules}>
              {savingModules ? 'Saving Configuration...' : 'Save Configuration & Limits'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Manage Plan & Subscription Modal */}
      <Modal isOpen={isSubModalOpen} toggle={() => setIsSubModalOpen(!isSubModalOpen)} centered>
        <ModalHeader toggle={() => setIsSubModalOpen(!isSubModalOpen)}>
          <i className="bx bx-credit-card text-primary me-2" /> Assign / Upgrade Tenant Subscription
        </ModalHeader>
        <Form onSubmit={handleAssignSub}>
          <ModalBody>
            <div className="mb-3 p-2 bg-light rounded font-size-12">
              <strong>Clinic:</strong> {selectedTenant?.clinic_name} ({selectedTenant?.email})
            </div>

            <FormGroup className="mb-3">
              <Label className="font-weight-semibold font-size-12">Subscription Tier Plan *</Label>
              <Input
                type="select"
                required
                value={subFormData.plan_id}
                onChange={(e) => setSubFormData({ ...subFormData, plan_id: e.target.value })}
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} (₹{p.price_monthly}/mo - ₹{p.price_yearly}/yr)
                  </option>
                ))}
              </Input>
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Billing Cycle</Label>
                  <Input
                    type="select"
                    value={subFormData.billing_cycle}
                    onChange={(e) => setSubFormData({ ...subFormData, billing_cycle: e.target.value })}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="lifetime">Lifetime</option>
                    <option value="trial">Trial</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Validity Duration (Days)</Label>
                  <Input
                    type="number"
                    value={subFormData.duration_days}
                    onChange={(e) => setSubFormData({ ...subFormData, duration_days: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-2">
              <Label className="font-weight-semibold font-size-12">Notes / Internal Reference</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="e.g. Upgrade authorized by SuperAdmin"
                value={subFormData.notes}
                onChange={(e) => setSubFormData({ ...subFormData, notes: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIsSubModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? 'Assigning...' : 'Confirm Subscription'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default TenantsManager;
